<script setup lang="ts">
import { computed } from 'vue'
import { useSyncStore } from '@/stores/sync'
import { useCaltopoStore } from '@/stores/caltopo'
import { useOpsStore } from '@/stores/ops'
import FosTooltip from '@/components/ui/FosTooltip.vue'

const syncStore = useSyncStore()
const caltopoStore = useCaltopoStore()
const opsStore = useOpsStore()

const caltopoConnected = computed(() => !!opsStore.selectedOp?.caltopo_map_id)

// Worst-case summary across all connections
const summary = computed<'ok' | 'pending' | 'error'>(() => {
  if (syncStore.status === 'offline' || (caltopoConnected.value && !!caltopoStore.error)) return 'error'
  if (syncStore.status === 'pending' || (caltopoConnected.value && caltopoStore.isPaused)) return 'pending'
  return 'ok'
})
</script>

<template>
  <FosTooltip side="bottom">
    <!-- Summary trigger -->
    <div class="flex items-center gap-1.5 cursor-default select-none">
      <span
        class="w-1.5 h-1.5"
        :class="{
          'bg-status-on-scene': summary === 'ok',
          'bg-status-rtb animate-pulse': summary === 'pending',
          'bg-status-emergency': summary === 'error',
        }"
        :data-testid="`dot-${summary}`"
      />
      <span
        class="font-mono text-[0.65rem] uppercase tracking-widest"
        :class="{
          'text-outline': summary !== 'error',
          'text-error': summary === 'error',
        }"
      >
        {{ summary === 'ok' ? 'CONNECTED' : summary === 'pending' ? 'PENDING' : 'ERROR' }}
      </span>
    </div>

    <!-- Hover breakdown -->
    <template #content>
      <div class="flex flex-col gap-2 min-w-[160px]">
        <!-- Firestore -->
        <div class="flex items-center justify-between gap-6">
          <span class="text-surface/50 uppercase tracking-widest text-[0.6rem]">Firestore</span>
          <div class="flex items-center gap-1.5">
            <span
              class="w-1.5 h-1.5 shrink-0"
              :class="{
                'bg-status-on-scene': syncStore.status === 'connected',
                'bg-status-rtb': syncStore.status === 'pending',
                'bg-status-emergency': syncStore.status === 'offline',
              }"
            />
            <span class="text-surface uppercase tracking-widest">
              {{ syncStore.status === 'connected' ? 'CONNECTED' : syncStore.status === 'pending' ? `PENDING (${syncStore.pendingWrites})` : 'OFFLINE' }}
            </span>
          </div>
        </div>

        <!-- CalTopo (only when a map is linked) -->
        <div v-if="caltopoConnected" class="flex items-center justify-between gap-6">
          <span class="text-surface/50 uppercase tracking-widest text-[0.6rem]">CalTopo</span>
          <div class="flex items-center gap-1.5">
            <span
              class="w-1.5 h-1.5 shrink-0"
              :class="{
                'bg-status-emergency': !!caltopoStore.error,
                'bg-status-rtb': !caltopoStore.error && caltopoStore.isPaused,
                'bg-status-on-scene animate-pulse': !caltopoStore.error && !caltopoStore.isPaused && caltopoStore.isSyncing,
                'bg-status-on-scene': !caltopoStore.error && !caltopoStore.isPaused && !caltopoStore.isSyncing,
              }"
            />
            <span class="text-surface uppercase tracking-widest">
              {{ caltopoStore.error ? 'ERROR' : caltopoStore.isPaused ? 'PAUSED' : caltopoStore.isSyncing ? 'SYNCING' : 'SYNCED' }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </FosTooltip>
</template>
